import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SuccessionPlanningPanel = ({ filters }) => {
  const [selectedPosition, setSelectedPosition] = useState(null);

  const criticalPositions = [];

  const highPotentialEmployees = [];

  const getRiskBadge = (risk) => {
    const badges = {
      critical: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-300', icon: 'AlertTriangle' },
      high: { label: 'High', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: 'AlertCircle' },
      medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: 'Info' },
      low: { label: 'Low', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: 'CheckCircle' }
    };
    return badges?.[risk] || badges?.medium;
  };

  const getReadinessColor = (readiness) => {
    if (readiness >= 80) return 'from-emerald-500 to-emerald-600';
    if (readiness >= 60) return 'from-blue-500 to-blue-600';
    if (readiness >= 40) return 'from-amber-500 to-amber-600';
    return 'from-red-500 to-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Icon name="Users" size={28} className="text-purple-600" />
              Succession Planning
            </h2>
            <p className="text-slate-600 mt-1">Identify high-potential employees and prepare succession for critical positions</p>
          </div>
          <Button variant="primary" iconName="Plus">
            Add Critical Position
          </Button>
        </div>

        {/* Planning Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-900">Critical Positions</span>
              <Icon name="Briefcase" size={18} className="text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-900">Succession Ready</span>
              <Icon name="CheckCircle" size={18} className="text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-emerald-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">High-Potential Staff</span>
              <Icon name="Star" size={18} className="text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-amber-900">In Development</span>
              <Icon name="TrendingUp" size={18} className="text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-amber-900">0</p>
          </div>
        </div>
      </div>
      {/* Critical Positions */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Icon name="Briefcase" size={24} className="text-slate-700" />
          Critical Positions & Succession Pipeline
        </h3>

        {criticalPositions?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="Briefcase" size={40} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Critical Positions Identified</h3>
            <p className="text-slate-600 mb-6">Start succession planning by identifying critical positions in your organization</p>
            <Button variant="primary" iconName="Plus">
              Add Critical Position
            </Button>
          </div>
        ) : (
        <div className="space-y-4">
          {criticalPositions?.map((position) => {
            const riskBadge = getRiskBadge(position?.riskLevel);
            return (
              <div key={position?.id} className="border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-slate-900">{position?.position}</h4>
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${riskBadge?.color}`}>
                        <Icon name={riskBadge?.icon} size={14} />
                        {riskBadge?.label} Risk
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Icon name="Building" size={14} />
                        <span>{position?.department}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="User" size={14} />
                        <span>Current: {position?.currentHolder}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        <span>Retirement: {new Date(position.retirementDate)?.toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Successors */}
                <div className="space-y-3 mt-4">
                  <p className="text-sm font-semibold text-slate-700">Identified Successors:</p>
                  {position?.successors?.map((successor, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-bold text-slate-900">{successor?.name}</p>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                              Successor #{idx + 1}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Icon name="Clock" size={14} />
                              <span>Time to Ready: {successor?.timeToReady}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-600 mb-1">Readiness Score</p>
                          <p className={`text-2xl font-bold bg-gradient-to-r ${getReadinessColor(successor?.readiness)} bg-clip-text text-transparent`}>
                            {successor?.readiness}%
                          </p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r ${getReadinessColor(successor?.readiness)} transition-all`}
                            style={{ width: `${successor?.readiness}%` }}
                          />
                        </div>
                      </div>

                      {/* Strengths & Gaps */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                          <p className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1">
                            <Icon name="CheckCircle" size={14} />
                            Key Strengths
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {successor?.strengths?.map((strength, i) => (
                              <span key={i} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">
                                {strength}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
                            <Icon name="AlertCircle" size={14} />
                            Development Gaps
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {successor?.gaps?.map((gap, i) => (
                              <span key={i} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">
                                {gap}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Development Plan */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-1">
                          <Icon name="Target" size={14} />
                          Development Plan
                        </p>
                        <p className="text-sm text-blue-800">{successor?.developmentPlan}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
      {/* High-Potential Employees */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Icon name="Star" size={24} className="text-amber-500" />
          High-Potential Employees
        </h3>

        {highPotentialEmployees?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="Star" size={40} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No High-Potential Staff Identified</h3>
            <p className="text-slate-600">Identify and develop high-potential employees for future leadership roles</p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {highPotentialEmployees?.map((employee) => (
            <div key={employee?.id} className="border border-slate-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-bold text-slate-900">{employee?.name}</h4>
                  <p className="text-sm text-slate-600">{employee?.currentPosition}</p>
                  <p className="text-xs text-slate-500">{employee?.department}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{employee?.potentialScore}</span>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">Career Path</p>
                <p className="text-sm text-blue-600 font-medium">{employee?.careerPath}</p>
              </div>

              <div className="mb-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">Key Strengths</p>
                <div className="flex flex-wrap gap-1">
                  {employee?.keyStrengths?.map((strength, i) => (
                    <span key={i} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">
                      {strength}
                    </span>
                  ))}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200">
                <p className="text-xs font-semibold text-slate-700 mb-1">Development Focus</p>
                <p className="text-sm text-slate-600">{employee?.developmentFocus}</p>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );
};

export default SuccessionPlanningPanel;